import data from '@emoji-mart/data'
  import { getEmojiDataFromNative, Store } from 'emoji-mart'

  import { CONSTS } from './objectConst'

  const STORAGE_KEY_VERSION = 'emoji-frequently-version'
  const STORAGE_KEY_FREQUENTLY = 'frequently'

  /**
   * Converts an array of emoji characters into a map of emoji IDs and their usage count.
   *
   * - Each emoji is assigned a usage count based on its position in the array (higher for earlier items).
   *
   * @param {string[]} emojis - An array of emoji characters.
   * @returns {Promise<Record<string, number>>} A promise that resolves to a map where keys are emoji IDs and values are their usage count.
   */
  const getFrequentEmojisMapFromArray = async (
    emojis: string[]
  ): Promise<Record<string, number>> => {
    const entries = await Promise.all(
      emojis.map(async (char, index, array) => {
        const emojiData = await getEmojiDataFromNative(char)
        const id = emojiData?.id || emojiData?.shortcodes?.[0] || char
        return [id, array.length - index] as [string, number]
      })
    )
    return Object.fromEntries(entries)
  }

  /**
   * Retrieves the top three frequently used emojis from the Emoji Mart store.
   *
   * - Sorts emojis by their usage count in descending order.
   * - Maps the top three emoji IDs to their native representations.
   * - Returns an empty array if no frequently used emojis are found.
   *
   * @returns {string[]} An array of up to three frequently used emojis in their native representation.
   */
  export const getPreviewEmojis = (): string[] => {
    const freq = Store.get(STORAGE_KEY_FREQUENTLY) as Record<string, number>

    if (!freq) {
      return []
    }

    const sortedIds = Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id)

    return sortedIds.slice(0, 3).map((id) => {
      // @ts-ignore
      return data.emojis[id]?.skins?.[0]?.native || id
    })
  }

  /**
   * Initializes the Emoji Mart library by setting up frequently used emojis.
   *
   * - Checks if the stored version of frequently used emojis matches the current version.
   * - If the versions match, no further action is taken.
   * - If the versions do not match, initializes the frequently used emojis with default values.
   * - Updates the local storage with the current version.
   *
   * @returns {Promise<void>} A promise that resolves when the initialization is complete.
   */
  export const initEmojiMart = async (): Promise<void> => {
    console.log('initEmojiMart')
    try {
      const savedVersion = Number(localStorage.getItem(STORAGE_KEY_VERSION))

      if (savedVersion && savedVersion === CONSTS.EMOJI.FREQUENTLY_USED_VERSION) {
        return
      }

      const freqMap = await getFrequentEmojisMapFromArray(
        CONSTS.EMOJI.DEFAULT_FREQUENTLY_USED_EMOJIS
      )

      // @ts-ignore
      Store.set(STORAGE_KEY_FREQUENTLY, freqMap)

      localStorage.setItem(
        STORAGE_KEY_VERSION,
        String(CONSTS.EMOJI.FREQUENTLY_USED_VERSION)
      )
    } catch (e) {
      console.error('EmojiMart init error:', e)
    }
  }